/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable functional/prefer-readonly-type */
/* eslint-disable max-lines-per-function */
import React, { useMemo } from 'react'
import { Card, CardContent, Typography, Box, useTheme } from '@mui/material'
import { ResponsiveHeatMap } from '@nivo/heatmap'
import { useLang } from '~/hooks/useLang'

interface HeatmapDataPoint {
  readonly weekday: string
  readonly hour: number
  readonly total?: number
  readonly value?: number
}

interface HeatmapChartProps {
  readonly data?: readonly HeatmapDataPoint[]
  readonly title: string
  readonly subtitle?: string
  readonly width?: number | string
  readonly height?: number
  readonly sxFont?: object
}

export default function HeatmapChart({
  data,
  title,
  subtitle,
  sxFont = {},
  width = '100%',
  height = 400,
}: HeatmapChartProps) {
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === 'dark'
  const { translate } = useLang()

  // Preparar dados no formato do Nivo
  const nivoData = useMemo(() => {
    if (!data || data.length === 0) return []

    // Obter weekdays únicos
    const uniqueWeekdays = Array.from(new Set(data.map((item) => item.weekday)))

    // Criar estrutura de dados para o Nivo
    // Cada objeto representa uma série (dia da semana) com array de dados
    return uniqueWeekdays.map((weekday) => ({
      id: weekday,
      data: Array.from({ length: 24 }, (_, hour) => {
        const dataPoint = data.find(
          (item) => item.weekday === weekday && item.hour === hour
        )
        return {
          x: `${hour}h`,
          y: dataPoint?.total || dataPoint?.value || 0,
        }
      }),
    }))
  }, [data])

  return (
    <Card sx={{ width, boxShadow: 3 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 'bold', ...sxFont }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="subtitle2" marginBottom={2} marginTop={1}>
            {subtitle}
          </Typography>
        )}

        <Box sx={{ height, mt: 2}}>
          {nivoData.length > 0 ? (
            <ResponsiveHeatMap
              data={nivoData}
              margin={{ top: 20, right: 20, bottom: 100, left: 110 }}
              valueFormat=">-.0f"
              axisTop={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -45,
              }}
              axisRight={null}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legendOffset: -90,
              }}
              colors={{
                type: 'sequential',
                scheme: 'orange_red',
              }}
              emptyColor={isDarkMode ?  '#1a1a1a': theme.palette.grey[100]}
              borderWidth={1}
              labelTextColor={(cell) => {
                const value = cell.data.y as number
                const maxValue = Math.max(...nivoData.flatMap(d => d.data.map(p => p.y as number)))
                const intensity = maxValue > 0 ? value / maxValue : 0
                return intensity > 0.5 ? '#ffffff' : '#000000'
              }}
              theme={{
                text: {
                  fill: theme.palette.text.primary,
                  fontFamily: '"Roboto", "Inter", "Helvetica", "Arial", sans-serif',
                  fontSize: 13,
                },
                tooltip: {
                  container: {
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    fontFamily: '"Roboto", "Inter", "Helvetica", "Arial", sans-serif',
                  },
                },
                axis: {
                  ticks: {
                    text: {
                      fontSize: 13,
                      fontFamily: '"Roboto", "Inter", "Helvetica", "Arial", "sans-serif"',
                    },
                  },
                },
              }}
              legends={[
                {
                  anchor: 'bottom',
                  translateX: 0,
                  translateY: 70,
                  length: 400,
                  thickness: 8,
                  direction: 'row',
                  tickPosition: 'after',
                  tickSize: 3,
                  tickSpacing: 4,
                  tickOverlap: false,
                  title: "Total de Despachos",
                  titleAlign: 'middle',
                  titleOffset: 4,
                },
              ]}
              animate={true}
              motionConfig="gentle"
              hoverTarget="rowColumn"
              tooltip={({ cell }) => (
                <Box
                  sx={{
                    background: theme.palette.background.paper,
                    padding: '10px 14px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '4px',
                    boxShadow: theme.shadows[3],
                    minWidth: '160px',
                    maxWidth: '220px',
                    transform: 'translate(-80px, -5px)',
                    pointerEvents: 'none',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {cell.serieId} - {cell.data.x}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {cell.value !== undefined && cell.value !== null
                      ? `${cell.value} despachos`
                      : 'Sem dados'}
                  </Typography>
                </Box>
              )}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Nenhum dado disponível
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}
